import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation, Link, useParams } from "react-router-dom";
import {
  SafText,
  SafTable,
  SafPagination,
  SafIcon,
  SafSearchField,
  SafCombobox,
  SafOption,
  SafCheckbox,
  SafButton,
  SafAnchor,
  SafAlert,
  SafDialog,
} from "@saffron/core-components/react";
import { Breadcrumb } from "../../../shared";
import "../../../App.css";
import "../../../css/table-responsive.css";
import { callMiddleware } from "../../../util/api";

// Cloud providers that are considered "major" - when one is selected, others are blocked
import {
  isMajorCloudProviderValue,
  matchesTechStackProvider,
} from "../../../util/constants";

const Addtechstack = () => {
  const dialogRef = useRef(null);
  const location = useLocation();
  const { assetId: urlAssetId } = useParams();

  // Get cloud provider from route state or sessionStorage (same key as Cloud Permit)
  const selectedCloudProvider =
    location.state?.cloudProvider ||
    sessionStorage.getItem(`selectedCloudProvider_${urlAssetId}`) ||
    "";
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [controlled, setControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [5, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 5,
  });
  const [selectedControlled, setSelectedControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [5, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 5,
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(null);
  const [UniqueCategories, setUniqueCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [environmentValue, setEnvironmentValue] = useState("Proposed");
  // NEW: Add pattern selection state variables
  const [selectedPatterns, setSelectedPatterns] = useState({}); // Track selected patterns per item
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeAllDialogOpen, setRemoveAllDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  // Add a key to force stable rendering
  const [renderKey, setRenderKey] = useState(0);

  const envData = ["Proposed", "Future"];

  const navigate = useNavigate();

  // Helper function to check if item is already selected
  const isItemSelected = (item) => {
    return selectedItems.some((selectedItem) => selectedItem.id === item.id);
  };

  // Add useEffect to prevent styling loss
  useEffect(() => {
    if (selectedItems.length === 0) {
      // Force a small re-render delay to maintain styling
      const timer = setTimeout(() => {
        setRenderKey((prev) => prev + 1);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [selectedItems.length]);

  // Listen for sidebar toggle changes to update table responsiveness
  useEffect(() => {
    const handleSidebarToggle = () => {
      setRenderKey((prev) => prev + 1);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          handleSidebarToggle();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const handleAddRuntime = async () => {
    if (!environmentValue) {
      setAlertMessage(
        "Environment is mandatory. Please select an environment."
      );
      setAlertType("warning");
      return;
    }

    if (selectedItems.length === 0) {
      setAlertMessage("Please select at least one item.");
      setAlertType("warning");
      return;
    }

    // UPDATED: Create services array with selected patterns instead of just service_ids
    const services = selectedItems.map((item) => ({
      service_id: item.techRadarId,
      pattern_ref_id:
        item.supportingPattern?.map((pattern) => pattern.pattern_ref_id) || [], // Extract only pattern names
    }));
    let deviationsToAdd = [];
    if (environmentValue === "Proposed") {
      deviationsToAdd = selectedItems
        .filter(
          (item) =>
            item.recommendation !== "Adopt" && item.recommendation !== "Mandate"
        )
        .map((item) => ({
          service_id: item.techRadarId,
          justification: "",
        }));
    }

    try {
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;
      const existingDataResult = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      let techStackData;

      if (
        existingDataResult &&
        Array.isArray(existingDataResult) &&
        existingDataResult.length > 0
      ) {
        techStackData = existingDataResult[0];
      } else if (
        existingDataResult &&
        existingDataResult.id === "technology-stacks"
      ) {
        techStackData = existingDataResult;
      } else {
        // UPDATED: Create new document with NEW structure using services
        techStackData = {
          id: "technology-stacks",
          asset_id: urlAssetId,
          technology_stacks: [
            {
              tech_stack: "frontend",
              tech_stack_environment: [
                {
                  environment: environmentValue,
                  score: "coming soon",
                  services: services, // Use services instead of service_ids
                },
              ],
            },
          ],
          deviations: deviationsToAdd.length > 0 ? [...deviationsToAdd] : [],
          createdAt: new Date().toISOString(),
          last_modified: new Date().toISOString(),
        };
      }

      if (techStackData.id === "technology-stacks") {
        techStackData.last_modified = new Date().toISOString();

        let techStack = techStackData.technology_stacks?.find(
          (stack) => stack.tech_stack === "frontend"
        );

        if (!techStack) {
          techStack = {
            tech_stack: "frontend",
            tech_stack_environment: [],
          };
          techStackData.technology_stacks =
            techStackData.technology_stacks || [];
          techStackData.technology_stacks.push(techStack);
        }

        let environment = techStack.tech_stack_environment?.find(
          (env) => env.environment === environmentValue
        );

        if (environment) {
          // UPDATED: Convert old structure to new structure if needed
          if (!environment.services && environment.service_ids) {
            environment.services = environment.service_ids.map((id) => ({
              service_id: id,
              pattern_ref_id: [],
            }));
            delete environment.service_ids; // Remove old property
          }

          // Initialize services array if it doesn't exist
          if (!environment.services) {
            environment.services = [];
          }

          // UPDATED: Add new services, avoiding duplicates
          services.forEach((newService) => {
            const existingService = environment.services.find(
              (s) => s.service_id === newService.service_id
            );
            if (!existingService) {
              environment.services.push(newService);
            } else {
              // Merge pattern_ref_id if service already exists
              const existingPatternRefIds =
                existingService.pattern_ref_id || [];
              const newPatternRefIds = newService.pattern_ref_id || [];

              newPatternRefIds.forEach((patternRefId) => {
                if (!existingPatternRefIds.includes(patternRefId)) {
                  existingService.pattern_ref_id.push(patternRefId);
                }
              });
            }
          });
        } else {
          // UPDATED: Create new environment with NEW structure
          techStack.tech_stack_environment =
            techStack.tech_stack_environment || [];
          techStack.tech_stack_environment.push({
            environment: environmentValue,
            score: "coming soon",
            services: services, // Use services instead of service_ids
          });
        }

        if (environmentValue === "Proposed") {
          if (techStackData.deviations) {
            deviationsToAdd.forEach((dev) => {
              if (
                !techStackData.deviations.some(
                  (d) => d.service_id === dev.service_id
                )
              ) {
                techStackData.deviations.push(dev);
              }
            });
          } else if (deviationsToAdd.length > 0) {
            techStackData.deviations = [...deviationsToAdd];
          }
        }
      }

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: techStackData,
        partitionKey: urlAssetId,
      });

      setAlertMessage("Technology added successfully.");
      setAlertType("success");
      setTimeout(() => {
        setAlertMessage("");
        navigate(`/${urlAssetId}/technology-stacks`);
      }, 4000);
    } catch (err) {
      let errorMessage = "Failed to add runtime. ";
      if (err.message) {
        errorMessage += err.message;
      }
      setAlertMessage(errorMessage);
      setAlertType("error");
    }
  };

  const fetchData = async () => {
    try {
      const dbName = process.env.REACT_APP_DB_SOURCE;
      const containerName = "tech-stack-mapping";

      const query = "SELECT * FROM c";
      const resources = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        maxItemCount: -1,
      });

      const resourceArray = Array.isArray(resources) ? resources : [resources];

      let transformedData = [];

      resourceArray.forEach((item, itemIndex) => {
        if (!item.capabilities) {
          return;
        }

        item.capabilities.forEach((capability, capabilityIndex) => {
          if (!capability.services) {
            return;
          }

          capability.services.forEach((service, serviceIndex) => {
            const supportingPatterns = service.supportingPattern || [];

            transformedData.push({
              id: `tech-stack-mapping-${itemIndex}-${capabilityIndex}-${serviceIndex}`,
              categoryName: item.category || "",
              technologyName: service.technology_name || "",
              capability: capability.capability || "",
              supportingPattern: supportingPatterns,
              recommendation: service.recommendation || "",
              provider: service.provider || "",
              techRadarId: service.tech_radar_id || "",
            });
          });
        });
      });

      setOriginalData(transformedData);
    } catch (error) {
      console.error("Error fetching data from middleware", error);
      setAlertMessage("Failed to fetch technology stack data.");
      setAlertType("error");
    }
  };

  // Filter data based on selected cloud provider.
  const filterByCloudProvider = useCallback(
    (dataToFilter) => {
      if (
        !selectedCloudProvider ||
        selectedCloudProvider === "All" ||
        selectedCloudProvider === ""
      ) {
        return dataToFilter;
      }

      return dataToFilter.filter((item) => {
        if (matchesTechStackProvider(item.provider, selectedCloudProvider)) {
          return true;
        }
        // Not the selected cloud — allow only if it's not another major cloud
        return !isMajorCloudProviderValue(item.provider);
      });
    },
    [selectedCloudProvider]
  );

  // Apply cloud provider filter whenever originalData or provider changes
  useEffect(() => {
    if (originalData.length > 0) {
      const filtered = filterByCloudProvider(originalData);
      setData(filtered);
    }
  }, [originalData, filterByCloudProvider]);

  useEffect(() => {
    fetchData();
  }, []);

  const onClear = useCallback(() => {
    setSearchValue("");
  }, []);

  const filteredData = useMemo(() => {
    if (!searchValue) return data;
    return data.filter((item) =>
      Object.values(item).some((value) =>
        value && typeof value === "string"
          ? value.toLowerCase().includes(searchValue.toLowerCase())
          : false
      )
    );
  }, [data, searchValue]);

  useEffect(() => {
    const filteredCount = filteredData.length;
    setControlled((prev) => ({
      ...prev,
      totalItemCount: filteredCount,
    }));
  }, [filteredData]);

  useEffect(() => {
    setSelectedControlled((prev) => ({
      ...prev,
      totalItemCount: selectedItems.length,
      currentPageIndex: 1,
    }));
  }, [selectedItems]);
  const handleAddItem = (item) => {
    const isAlreadySelected = selectedItems.some(
      (selectedItem) => selectedItem.id === item.id
    );

    if (!isAlreadySelected) {
      // Get selected patterns for this item
      const selectedPatternIndices = selectedPatterns[item.id] || [];

      // Filter supportingPattern to include only selected ones
      const filteredSupportingPattern = item.supportingPattern
        ? item.supportingPattern.filter((_, index) =>
            selectedPatternIndices.includes(index)
          )
        : [];

      // Create new item with filtered patterns
      const itemWithSelectedPatterns = {
        ...item,
        supportingPattern: filteredSupportingPattern,
      };

      setSelectedItems((prev) => [...prev, itemWithSelectedPatterns]);
      // Clear pattern selections for this item after adding
      setSelectedPatterns((prev) => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });

      setAlertType("success");
      setTimeout(() => setAlertMessage(""), 2000);
    } else {
      setAlertMessage("Item is already selected.");
      setAlertType("warning");
      setTimeout(() => setAlertMessage(""), 2000);
    }
  };

  // NEW: Add function to handle pattern changes in selected items
  const handleSelectedItemPatternToggle = (
    itemId,
    patternIndex,
    isChecked,
    originalItem
  ) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id !== itemId) return item;

        const pattern = originalItem.supportingPattern[patternIndex];
        let updatedPatterns;

        if (isChecked) {
          // Add pattern if not already present
          const patternExists = item.supportingPattern.some(
            (p) => p.pattern_name === pattern.pattern_name
          );
          updatedPatterns = patternExists
            ? item.supportingPattern
            : [...item.supportingPattern, pattern];
        } else {
          // Remove pattern
          updatedPatterns = item.supportingPattern.filter(
            (p) => p.pattern_name !== pattern.pattern_name
          );
        }

        return { ...item, supportingPattern: updatedPatterns };
      })
    );
  };

  // NEW: Add helper function to handle pattern selection
  const handlePatternToggle = (itemId, patternIndex, isChecked) => {
    setSelectedPatterns((prev) => {
      const updated = { ...prev };
      if (!updated[itemId]) {
        updated[itemId] = [];
      }

      if (isChecked) {
        // Add pattern index if not already present
        if (!updated[itemId].includes(patternIndex)) {
          updated[itemId] = [...updated[itemId], patternIndex];
        }
      } else {
        // Remove pattern index
        updated[itemId] = updated[itemId].filter(
          (index) => index !== patternIndex
        );
      }

      // Clean up empty arrays
      if (updated[itemId].length === 0) {
        delete updated[itemId];
      }

      return updated;
    });
  };

  const handleRemoveItem = (itemId) => {
    setItemToRemove(itemId);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveItem = () => {
    if (itemToRemove) {
      setSelectedItems((prev) =>
        prev.filter((item) => item.id !== itemToRemove)
      );
      setAlertMessage("Item removed from selection.");
      setAlertType("success");
      setTimeout(() => setAlertMessage(""), 2000);
    }
    setRemoveDialogOpen(false);
    setItemToRemove(null);
  };

  const cancelRemoveItem = () => {
    setRemoveDialogOpen(false);
    setItemToRemove(null);
  };
  const handleRemoveAll = () => {
    if (selectedItems.length === 0) {
      setAlertMessage("No items to remove.");
      setAlertType("warning");
      setTimeout(() => setAlertMessage(""), 2000);
      return;
    }
    setRemoveAllDialogOpen(true);
  };

  const confirmRemoveAll = () => {
    setSelectedItems([]);
    setAlertMessage("All items removed from selection.");
    setAlertType("success");
    setTimeout(() => setAlertMessage(""), 2000);
    setRemoveAllDialogOpen(false);
  };

  const cancelRemoveAll = () => {
    setRemoveAllDialogOpen(false);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, controlled.currentPageIndex, controlled.itemsPerPage]);

  const paginatedSelectedItems = useMemo(() => {
    const startIndex =
      (selectedControlled.currentPageIndex - 1) *
      selectedControlled.itemsPerPage;
    const endIndex = startIndex + selectedControlled.itemsPerPage;
    return selectedItems.slice(startIndex, endIndex);
  }, [
    selectedItems,
    selectedControlled.currentPageIndex,
    selectedControlled.itemsPerPage,
  ]);

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(prev.totalItemCount / prev.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: event.detail,
    }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  const onSelectedPrevious = () => {
    setSelectedControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onSelectedNext = () => {
    setSelectedControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(prev.totalItemCount / prev.itemsPerPage)
      ),
    }));
  };

  const onSelectedChange = (event) => {
    setSelectedControlled((prev) => ({
      ...prev,
      currentPageIndex: event.detail,
    }));
  };

  const onSelectedItemsPerPageChange = (event) => {
    setSelectedControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (dialog) {
      dialog.addEventListener("hide", closeDialog);

      return () => {
        dialog.removeEventListener("hide", closeDialog);
      };
    }
  }, [isDialogOpen]);

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleFilterClick = () => {
    setIsDialogOpen(true);
    setTimeout(() => {
      dialogRef?.current?.focus();
    });
  };

  const getUniqueCategories = useCallback(() => {
    const categories = [
      ...new Set(originalData.map((item) => item.categoryName)),
    ];
    setUniqueCategories(categories.filter(Boolean));
  }, [originalData]);

  useEffect(() => {
    getUniqueCategories();
  }, [originalData, getUniqueCategories]);

  const handleCategorySelect = (capability) => {
    setSelectedCategories((prev) => {
      if (prev.includes(capability)) {
        return prev.filter((cap) => cap !== capability);
      }
      return [...prev, capability];
    });
  };

  const handleApplyFilter = () => {
    const providerFiltered = filterByCloudProvider(originalData);
    if (selectedCategories.length > 0) {
      const filteredData = providerFiltered.filter((item) =>
        selectedCategories.includes(item.categoryName)
      );
      setData(filteredData);
    } else {
      setData(providerFiltered);
    }
    closeDialog();
  };

  return (
    <div
      style={{ marginTop: "85px", padding: "16px" }}
      key={`main-container-${renderKey}`}
      className="content-container"
    >
      <div className="breadcrumb-section">
        <Breadcrumb />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <SafText appearance="heading-lg">
          Add a technology to your Technology Stack
        </SafText>
        {selectedCloudProvider && selectedCloudProvider !== "All" && selectedCloudProvider !== "" && (
          <SafText
            appearance="body-default-lg"
            style={{
              display: "flex",
              alignItems: "center",
              color: "#0066cc",
              fontWeight: "bold",
            }}
          >
            Filtered by: {selectedCloudProvider}
          </SafText>
        )}
      </div>
      <br />

      <div style={{ width: "100%", margin: "0" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <SafCombobox
            id="combobox-environment"
            name="environment-backend"
            onChange={(event) => setEnvironmentValue(event.target.value)}
            value={environmentValue}
            style={{ width: "300px" }}
            onFocus={(event) => {
              // Clear the input to show all options
              event.target.value = "";
            }}
            onBlur={(event) => {
              // Reset to default if nothing selected
              if (!event.target.value) {
                setEnvironmentValue("Proposed");
              }
            }}
          >
            {envData.map((env, index) => (
              <SafOption
                key={index}
                onClick={() => {
                  setEnvironmentValue(env);
                  document.getElementById("combobox-environment").blur(); // Close dropdown
                }}
              >
                {env}
              </SafOption>
            ))}
          </SafCombobox>
          <SafSearchField
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onClear={onClear}
            label="Search"
            style={{ width: "300px" }}
          />
        </div>

        {/* Available Technologies Table with stable key */}
        <div
          key={`available-technologies-${renderKey}`}
          className="table-container"
        >
          <div style={{ marginBottom: "0.5rem" }}>
            <SafText appearance="heading-lg">Available Technologies</SafText>
          </div>
          <SafTable
            key={`main-table-${renderKey}`}
            alternating-rows="true"
            density="inherit"
            header-background="default"
            inline-borders="true"
            style={{
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <table className="mytable">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>Add</th>
                  <th
                    onClick={() => handleSort("technologyName")}
                    style={{ width: "200px" }}
                  >
                    Technology Name
                    <SafIcon
                      iconName={
                        sortConfig.key === "technologyName"
                          ? sortConfig.direction === "ascending"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleSort("Category")}
                    style={{ width: "200px" }}
                  >
                    Category
                    <SafIcon iconName="filter" onClick={handleFilterClick} />
                  </th>
                  <th
                    onClick={() => handleSort("Capability")}
                    style={{ width: "200px" }}
                  >
                    Capability
                  </th>
                  <th style={{ width: "200px" }}>Supporting Patterns</th>
                  <th
                    onClick={() => handleSort("recommendation")}
                    style={{ width: "200px" }}
                  >
                    Recommendation
                    <SafIcon
                      iconName={
                        sortConfig.key === "recommendation"
                          ? sortConfig.direction === "ascending"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleSort("provider")}
                    style={{ width: "200px" }}
                  >
                    Provider
                    <SafIcon
                      iconName={
                        sortConfig.key === "provider"
                          ? sortConfig.direction === "ascending"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={`${item.id}-${index}`}>
                    <td>
                      <SafIcon
                        iconName="plus"
                        style={{
                          cursor: isItemSelected(item)
                            ? "not-allowed"
                            : "pointer",
                          color: isItemSelected(item) ? "#ccc" : "#007bff",
                          opacity: isItemSelected(item) ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (!isItemSelected(item)) {
                            handleAddItem(item);
                          }
                        }}
                      />
                    </td>
                    <td>{item.technologyName || ""}</td>
                    <td>{item.categoryName || ""}</td>
                    <td>{item.capability || ""}</td>
                    {/* UPDATED: Supporting Patterns column with checkboxes */}
                    <td style={{ minWidth: "250px", lineHeight: "1.2" }}>
                      {item.supportingPattern &&
                      item.supportingPattern.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {item.supportingPattern.map(
                            (pattern, patternIndex) => (
                              <div
                                key={patternIndex}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <SafCheckbox
                                  checked={
                                    selectedPatterns[item.id]?.includes(
                                      patternIndex
                                    ) || false
                                  }
                                  onChange={(e) =>
                                    handlePatternToggle(
                                      item.id,
                                      patternIndex,
                                      e.target.checked
                                    )
                                  }
                                />
                                <SafAnchor
                                  href={`https://techtoc.thomsonreuters.com/${pattern.pattern_link}/`}
                                  target="_blank"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  {pattern.pattern_name}
                                </SafAnchor>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>{item.recommendation || ""}</td>
                    <td>{item.provider || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SafTable>
          <div className="Component-demo" key={`main-pagination-${renderKey}`}>
            <SafPagination
              data-testid="controlled"
              controlled={true}
              totalItemCount={controlled.totalItemCount}
              currentPageIndex={controlled.currentPageIndex}
              itemsArray={controlled.itemsArray}
              itemsPerPage={controlled.itemsPerPage}
              onPrevious={onPrevious}
              onNext={onNext}
              onChange={onChange}
              onItemsPerPageChange={onItemsPerPageChange}
              style={{ width: "95%" }}
            />
          </div>
        </div>

        {/* Selected Items Section with stable key */}
        <div key="selected-items-section" style={{ marginTop: "1.5rem" }}>
          <br />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <SafText appearance="heading-lg">
              Selected Items ({selectedItems.length})
            </SafText>
            {selectedItems.length > 0 && (
              <SafButton appearance="secondary" onClick={handleRemoveAll}>
                Remove All
              </SafButton>
            )}
          </div>

          <div
            key={`selected-table-container-${
              selectedItems.length > 0 ? "with-data" : "empty"
            }`}
          >
            {selectedItems.length > 0 ? (
              <>
                <SafTable
                  key="selected-table"
                  alternating-rows="true"
                  density="inherit"
                  header-background="default"
                  inline-borders="true"
                  style={{ width: "100%" }}
                >
                  <table className="mytable">
                    <thead>
                      <tr>
                        <th style={{ width: "200px" }}>Technology Name</th>
                        <th style={{ width: "200px" }}>Category</th>
                        <th style={{ width: "200px" }}>Capability</th>
                        <th style={{ width: "200px" }}>Supporting Patterns</th>
                        <th style={{ width: "200px" }}>Recommendation</th>
                        <th style={{ width: "200px" }}>Provider</th>
                        <th style={{ width: "50px" }}>Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSelectedItems.map((item, index) => {
                        // Find the original item to get all available patterns
                        const originalItem = originalData.find(
                          (orig) => orig.id === item.id
                        );

                        return (
                          <tr key={`selected-${item.id}-${index}`}>
                            <td>{item.technologyName || ""}</td>
                            <td>{item.categoryName || ""}</td>
                            <td>{item.capability || ""}</td>
                            {/* UPDATED: Supporting Patterns column with checkboxes for selected items */}
                            <td
                              style={{ minWidth: "250px", lineHeight: "1.2" }}
                            >
                              {originalItem?.supportingPattern &&
                              originalItem.supportingPattern.length > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                  }}
                                >
                                  {originalItem.supportingPattern.map(
                                    (pattern, patternIndex) => (
                                      <div
                                        key={patternIndex}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <SafCheckbox
                                          checked={item.supportingPattern.some(
                                            (selectedPattern) =>
                                              selectedPattern.pattern_name ===
                                              pattern.pattern_name
                                          )}
                                          onChange={(e) =>
                                            handleSelectedItemPatternToggle(
                                              item.id,
                                              patternIndex,
                                              e.target.checked,
                                              originalItem
                                            )
                                          }
                                        />
                                        <SafAnchor
                                          href={`https://techtoc.thomsonreuters.com/${pattern.pattern_link}/`}
                                          target="_blank"
                                          style={{ fontSize: "0.875rem" }}
                                        >
                                          {pattern.pattern_name}
                                        </SafAnchor>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                            <td>{item.recommendation || ""}</td>
                            <td>{item.provider || ""}</td>
                            <td>
                              <SafIcon
                                iconName="close"
                                style={{ cursor: "pointer", color: "#dc3545" }}
                                onClick={() => handleRemoveItem(item.id)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </SafTable>
                <div className="Component-demo" key="selected-pagination">
                  <SafPagination
                    data-testid="selected-controlled"
                    controlled={true}
                    totalItemCount={selectedControlled.totalItemCount}
                    currentPageIndex={selectedControlled.currentPageIndex}
                    itemsArray={selectedControlled.itemsArray}
                    itemsPerPage={selectedControlled.itemsPerPage}
                    onPrevious={onSelectedPrevious}
                    onNext={onSelectedNext}
                    onChange={onSelectedChange}
                    onItemsPerPageChange={onSelectedItemsPerPageChange}
                    style={{ width: "95%" }}
                  />
                </div>
              </>
            ) : (
              <div
                key="empty-state"
                style={{
                  padding: "20px",
                  textAlign: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <SafText appearance="body-lg" style={{ color: "#666" }}>
                  No items selected. Use the + icon above to add technologies to
                  your selection.
                </SafText>
              </div>
            )}
          </div>
        </div>
      </div>

      {alertMessage && (
        <div style={{ width: "50%", textAlign: "center", margin: "20px auto" }}>
          <SafAlert
            type="toast"
            appearance={alertType}
            onClose={() => setAlertMessage("")}
            duration="5"
          >
            {alertMessage}
          </SafAlert>
        </div>
      )}

      {/* Remove Single Item Dialog */}
      {removeDialogOpen && (
        <SafDialog
          id="remove-item-dialog"
          modal={true}
          isAlert="true"
          isHeader="true"
          isFooter="true"
          dialogTitle="Confirm Removal"
          hidden={false}
        >
          <div style={{ padding: "20px" }}>
            <SafText appearance="body-lg">
              Are you sure you want to remove this item?
            </SafText>
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              padding: "20px",
            }}
          >
            <SafButton appearance="secondary" onClick={cancelRemoveItem}>
              No
            </SafButton>
            <SafButton appearance="primary" onClick={confirmRemoveItem}>
              Yes
            </SafButton>
          </div>
        </SafDialog>
      )}

      {/* Remove All Items Dialog */}
      {removeAllDialogOpen && (
        <SafDialog
          id="remove-all-dialog"
          modal={true}
          isAlert="true"
          isHeader="true"
          isFooter="true"
          dialogTitle="Confirm Remove All"
          hidden={false}
        >
          <div style={{ padding: "20px" }}>
            <SafText appearance="body-lg">
              Are you sure you want to remove all selected items?
            </SafText>
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              padding: "20px",
            }}
          >
            <SafButton appearance="secondary" onClick={cancelRemoveAll}>
              No
            </SafButton>
            <SafButton appearance="primary" onClick={confirmRemoveAll}>
              Yes
            </SafButton>
          </div>
        </SafDialog>
      )}

      {isDialogOpen && (
        <SafDialog
          id="filter-dialog-overlay"
          modal={false}
          isAlert="false"
          isHeader="true"
          isFooter="true"
          noFocusTrap={false}
          dialogTitle="Filter by Category"
          ref={dialogRef}
          hidden={false}
          triggerId="filter-dialog-trigger"
        >
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <SafButton appearance="secondary" onClick={closeDialog}>
              Cancel
            </SafButton>
            <SafButton appearance="primary" onClick={handleApplyFilter}>
              Apply Filter
            </SafButton>
          </div>
          <div
            style={{ padding: "20px", maxHeight: "200px", overflowY: "auto" }}
          >
            {UniqueCategories.map((category, index) => (
              <div key={index} style={{ marginBottom: "10px" }}>
                <SafCheckbox
                  checked={selectedCategories.includes(category)}
                  onChange={() => handleCategorySelect(category)}
                >
                  {category}
                </SafCheckbox>
              </div>
            ))}
          </div>
        </SafDialog>
      )}

      <hr style={{ margin: "20px 0 20px 0" }}></hr>
      <div style={{ width: "70%" }}>
        <div style={{ justifyContent: "space-between" }}>
          <Link to={`/${urlAssetId}/technology-stacks`}>
            <SafButton appearance="secondary">Cancel</SafButton>
          </Link>
          <SafButton
            appearance="primary"
            style={{ marginLeft: "20px" }}
            onClick={handleAddRuntime}
          >
            Add
          </SafButton>
        </div>
      </div>
    </div>
  );
};

export default Addtechstack;
